import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', slug: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/categories`, formData, {
        withCredentials: true
      });
      setFormData({ name: '', slug: '' });
      setShowForm(false);
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Failed to create category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will affect related reviews.')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/categories/${id}`, {
        withCredentials: true
      });
      setCategories(categories.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  if (loading) {
    return <div data-testid="loading-state">Loading...</div>;
  }

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl font-bold">Categories</h1>
        <Button
          onClick={() => setShowForm(!showForm)}
          data-testid="toggle-category-form"
          className="flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Category</span>
        </Button>
      </div>

      {showForm && (
        <div className="bg-card border border-border p-6 mb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <Input
                data-testid="category-name-input"
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setFormData({ name, slug: generateSlug(name) });
                }}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Slug</label>
              <Input
                data-testid="category-slug-input"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                required
              />
            </div>
            <div className="flex space-x-3">
              <Button type="submit" data-testid="category-submit-btn">
                Create Category
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-card border border-border">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold">Name</th>
              <th className="text-left p-4 font-semibold">Slug</th>
              <th className="text-left p-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan="3" className="p-8 text-center text-muted-foreground">
                  No categories yet
                </td>
              </tr>
            ) : (
              categories.map((category) => (
                <tr key={category.id} data-testid={`category-row-${category.id}`} className="border-b border-border hover:bg-muted">
                  <td className="p-4 font-medium">{category.name}</td>
                  <td className="p-4">{category.slug}</td>
                  <td className="p-4">
                    <button
                      onClick={() => handleDelete(category.id)}
                      data-testid={`delete-category-${category.id}`}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCategories;